import { createRouter, createWebHistory } from "vue-router";
import MenuView from "../views/menu.vue";
import { db, getCurrentUser } from "@/services/firebase";
import { getDoc, doc } from "firebase/firestore";
import { useNotification } from "@/services/notificationService";

// Menu is the landing route, so it ships in the main bundle. The rest are
// lazy-loaded: /admin in particular is dead weight for almost every visitor.
const routes = [
  {
    path: "/",
    redirect: "/menu",
  },
  {
    path: "/menu",
    name: "menu",
    component: MenuView,
    meta: {
      requiresAuth: false, // Menu is accessible without login
      title: "Menu",
    },
  },
  {
    path: "/editor",
    name: "editor",
    component: () => import(/* webpackChunkName: "editor" */ "../views/editor.vue"),
    meta: {
      requiresAuth: true,
      title: "Editor",
    },
  },
  {
    path: "/settings",
    name: "settings",
    component: () =>
      import(/* webpackChunkName: "settings" */ "../views/UserSettingsView.vue"),
    meta: {
      requiresAuth: true,
      title: "User Settings",
    },
  },
  {
    path: "/contact",
    name: "Contact",
    component: () => import(/* webpackChunkName: "contact" */ "../views/ContactView.vue"),
    meta: {
      requiresAuth: false,
      title: "Contact",
    },
  },
  {
    path: "/privacy",
    name: "privacy",
    component: () => import(/* webpackChunkName: "privacy" */ "../views/PrivacyView.vue"),
    meta: {
      requiresAuth: false,
      title: "Privacy Policy",
    },
  },
  {
    path: "/admin",
    name: "admin",
    component: () => import(/* webpackChunkName: "admin" */ "../views/AdminView.vue"),
    meta: {
      requiresAuth: true,
      requiresAdmin: true, // Admin-only route
      title: "Admin Dashboard",
    },
  },
  // Anything else is a typo or a stale link - send it to the menu rather than
  // rendering a blank page under the navbar.
  {
    path: "/:pathMatch(.*)*",
    redirect: "/menu",
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    return savedPosition || { top: 0 };
  },
});

// Navigation Guard for Auth and Admin Routes
router.beforeEach(async (to, from, next) => {
  const { displayNotification } = useNotification();

  // Wait for Firebase to restore any persisted session. If that lookup fails
  // outright (offline, blocked request) treat it as signed out rather than
  // letting the rejection abort navigation and leave the user on a dead page.
  let user = null;
  try {
    user = await getCurrentUser();
  } catch (error) {
    console.error("Could not resolve auth state:", error.message);
  }
  const isLoggedIn = !!user;

  if (to.meta.requiresAuth && !isLoggedIn) {
    displayNotification("You need to log in to access this functionality.");
    next({ name: "menu" });
  } else if (to.meta.requiresAdmin) {
    if (isLoggedIn) {
      // The real enforcement is in firestore.rules - this read only decides
      // whether to bother rendering the dashboard.
      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists() && userDoc.data().isAdmin) {
          next();
        } else {
          displayNotification("Access Denied: Admins only.");
          next("/settings");
        }
      } catch (error) {
        displayNotification("Access Denied: Admins only.");
        next("/settings");
      }
    } else {
      displayNotification("You must be logged in to access this page.");
      next("/settings");
    }
  } else {
    next();
  }
});

router.afterEach((to) => {
  document.title = to.meta.title
    ? `${to.meta.title} | FoodPlanner`
    : "FoodPlanner - weekly dinner planning";
});

export default router;
